import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';

import {
  reportCollectionsFilterSelector,
  reportCollectionsFilterGroupBySelector,
  reportFilterCountryCodeSelector,
  reportFilterSelector,
  reportReliefsSelector,
  downloadReportPdf,
} from '../../state';
import { Relief } from '../../types';
import { getAccountingInformationReportPdfFilters } from '../AccountingInformationReportDataGrids/getPdfFilters';
import handlerFactory, { ReliefsDataGridContext } from './handlerFactory';

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<Relief, ReliefsDataGridContext>();

  const { t } = useTranslation();
  const title = t('reliefs.title');
  const country = useSelector(reportFilterCountryCodeSelector);
  const reliefsResource = useSelector(reportReliefsSelector);
  const groupBy = useSelector(reportCollectionsFilterGroupBySelector);
  const reportCollectionsFilters = useSelector(reportCollectionsFilterSelector);

  const { data, loading } = reliefsResource;

  const gridContext = useMemo(
    () => ({
      t,
      country,
      groupBy,
    }),
    [t, country, groupBy]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title,
    loading,
    filters: reportCollectionsFilters,
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<Relief, ReliefsDataGridContext>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reportFilters = useSelector(reportFilterSelector);
  const reportCollectionsFilters = useSelector(reportCollectionsFilterSelector);

  useEffect(() => {
    handler.setExportConfig({
      PDF: {
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: [
                'countryCode',
                'stores',
                'pos',
                'salesTypes',
                'segments',
                'timeBand',
                'hourRange',
                'startBusinessDate',
                'endBusinessDate',
                'groupBy',
              ],
              opStructures: [
                'regionalMgmt',
                'region',
                'management',
                'supervision',
                'agreement',
                'company',
              ],
              filters: getAccountingInformationReportPdfFilters(
                reportFilters,
                reportCollectionsFilters
              ),
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [handler, dispatch, t, reportFilters, reportCollectionsFilters]);

  return null;
};

const ReliefsDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory}>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default ReliefsDataGrid;
